import { useEffect } from "react";

/**
 * Rende ogni tabella dell'app ordinabile (clic sull'intestazione) e con colonne
 * ridimensionabili trascinando i bordi. Le larghezze restano salvate per tabella.
 */
const KEY = "bc_table_widths";
const state = new WeakMap();
let applying = false;
let resizing = false;

const loadWidths = () => { try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; } };
const saveWidths = (all) => localStorage.setItem(KEY, JSON.stringify(all));

const tableId = (table) => {
  const heads = [...table.querySelectorAll("thead th")].map((th) => {
    const clone = th.cloneNode(true);
    clone.querySelectorAll("[data-bc-arrow],[data-bc-handle]").forEach((x) => x.remove());
    return clone.textContent.trim();
  }).join("|");
  return `${window.location.pathname}::${heads}`.slice(0, 300);
};

const cellValue = (row, idx) => {
  const td = row.children[idx];
  if (!td) return "";
  const clone = td.cloneNode(true);
  clone.querySelectorAll("[data-bc-arrow],[data-bc-handle]").forEach((x) => x.remove());
  return clone.textContent.trim();
};

const asNumber = (s) => {
  const t = s.replace(/[^\d,.\-]/g, "");
  if (!t) return null;
  const n = Number(t.replace(/\./g, "").replace(",", "."));
  return Number.isNaN(n) ? null : n;
};

const asDate = (s) => {
  const m = s.match(/(\d{2})[/.](\d{2})[/.](\d{4})/);
  if (m) return Number(`${m[3]}${m[2]}${m[1]}`);
  const i = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return i ? Number(`${i[1]}${i[2]}${i[3]}`) : null;
};

const compare = (a, b) => {
  const da = asDate(a), dbb = asDate(b);
  if (da !== null && dbb !== null) return da - dbb;
  const na = asNumber(a), nb = asNumber(b);
  if (na !== null && nb !== null) return na - nb;
  return a.localeCompare(b, "it", { sensitivity: "base", numeric: true });
};

const applySort = (table) => {
  const st = state.get(table);
  if (!st || st.sortIdx == null) return;
  const tbody = table.querySelector("tbody");
  if (!tbody) return;
  const rows = [...tbody.rows].filter((r) => !r.dataset.noSort && r.children.length > st.sortIdx);
  if (rows.length < 2) return;
  const sorted = [...rows].sort((r1, r2) =>
    st.dir * compare(cellValue(r1, st.sortIdx), cellValue(r2, st.sortIdx)));
  applying = true;
  sorted.forEach((r) => tbody.appendChild(r));
  applying = false;
};

const cleanText = (el) => {
  const clone = el.cloneNode(true);
  clone.querySelectorAll("[data-bc-arrow],[data-bc-handle]").forEach((x) => x.remove());
  return clone.textContent.trim();
};

/**
 * Su mobile le tabelle con molte colonne diventano card verticali: ogni cella
 * mostra l'etichetta dell'intestazione accanto al valore (vedi index.css).
 */
const cardify = (table) => {
  const thead = table.querySelector("thead");
  const tbody = table.querySelector("tbody");
  if (!thead || !tbody) return;
  if (table.closest("[data-testid='pdf-viewer']")) return;
  const ths = [...thead.querySelectorAll("tr:last-child th")];
  // restano tabelle scorrevoli: le griglie a matrice (calendario) e quelle di inserimento dati
  const esclusa = table.classList.contains("bc-no-cards") || ths.length > 12 ||
    tbody.querySelector("input, select, textarea, [contenteditable='true']") !== null;
  const attivo = window.innerWidth < 768 && ths.length >= 4 && !esclusa;
  table.classList.toggle("bc-cards", attivo);
  const righe = [...tbody.rows];
  if (!attivo) {
    righe.forEach((r) => {
      delete r.dataset.bcFull;
      [...r.cells].forEach((td) => { delete td.dataset.bcLabel; delete td.dataset.bcActions; });
    });
    return;
  }
  const etichette = ths.map(cleanText);
  righe.forEach((r) => {
    if ([...r.cells].some((td) => td.colSpan > 1)) {
      r.dataset.bcFull = "1";
      return;
    }
    delete r.dataset.bcFull;
    [...r.cells].forEach((td, i) => {
      td.dataset.bcLabel = etichette[i] || "";
      const azioni = td.querySelectorAll("button,[role='button'],a[href]").length;
      if (azioni >= 2) td.dataset.bcActions = "1";
      else delete td.dataset.bcActions;
    });
  });
};

const decorate = (table) => {
  const thead = table.querySelector("thead");
  const tbody = table.querySelector("tbody");
  if (!thead || !tbody || tbody.rows.length < 1) return;
  if (table.closest("[data-testid='pdf-viewer']")) return;
  const ths = [...thead.querySelectorAll("tr:last-child th")];
  if (ths.length < 2) return;

  const id = tableId(table);
  let st = state.get(table);
  if (!st || st.id !== id) {
    st = { id, sortIdx: null, dir: 1 };
    state.set(table, st);
  }
  const widths = loadWidths();
  const isMobile = window.innerWidth < 768;
  // su mobile le larghezze fisse salvate da desktop schiacciano le colonne: si usa il contenuto
  table.style.tableLayout = isMobile ? "auto" : "fixed";
  // con larghezze fisse la tabella non deve più riempire il 100%: così ridimensionando
  // una colonna le altre non vengono ricalcolate
  const bloccata = !isMobile && Object.keys(widths[id] || {}).length > 0;
  table.style.width = bloccata ? "auto" : "100%";
  table.style.minWidth = "";

  ths.forEach((th, idx) => {
    const saved = widths[id]?.[idx];
    if (saved && !isMobile) th.style.width = `${saved}px`;
    else if (isMobile) th.style.width = "";

    if (!th.dataset.bcSort) {
      th.dataset.bcSort = "1";
      th.style.position = th.style.position || "relative";
      th.style.cursor = "pointer";
      th.style.userSelect = "none";
      th.title = "Clicca per ordinare";
      th.addEventListener("click", (e) => {
        if (e.target.closest?.("[data-bc-handle]")) return;
        const s = state.get(table);
        s.dir = s.sortIdx === idx ? -s.dir : 1;
        s.sortIdx = idx;
        ths.forEach((o, i) => {
          const arrow = o.querySelector("[data-bc-arrow]");
          if (arrow) arrow.textContent = i === idx ? (s.dir === 1 ? " ▲" : " ▼") : "";
        });
        applySort(table);
      });
      const arrow = document.createElement("span");
      arrow.setAttribute("data-bc-arrow", "1");
      arrow.style.cssText = "font-size:9px;opacity:.75;margin-left:2px";
      th.appendChild(arrow);
    }

    if (idx < ths.length - 1 && !isMobile && !th.querySelector("[data-bc-handle]")) {
      const handle = document.createElement("span");
      handle.dataset.bcHandle = "1";
      handle.setAttribute("data-bc-handle", "1");
      handle.style.cssText = "position:absolute;top:0;right:-4px;width:9px;height:100%;cursor:col-resize;z-index:5;display:flex;align-items:center;justify-content:center";
      const linea = document.createElement("span");
      linea.setAttribute("data-bc-handle", "1");
      linea.dataset.bcHandle = "1";
      linea.style.cssText = "pointer-events:none;width:2px;height:60%;border-radius:2px;background:currentColor;opacity:.35;transition:opacity .15s,height .15s";
      handle.appendChild(linea);
      handle.addEventListener("mouseenter", () => { linea.style.opacity = ".9"; linea.style.height = "100%"; });
      handle.addEventListener("mouseleave", () => { linea.style.opacity = ".35"; linea.style.height = "60%"; });
      handle.title = "Trascina per ridimensionare la colonna";
      handle.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        resizing = true;
        const startX = e.clientX;
        const startW = th.getBoundingClientRect().width;
        // si fissano in pixel tutte le colonne: da qui in poi solo quella trascinata cambia
        const bloccate = ths.map((o) => Math.round(o.getBoundingClientRect().width));
        ths.forEach((o, i) => { o.style.width = `${bloccate[i]}px`; });
        const totale = bloccate.reduce((a, b) => a + b, 0);
        table.style.width = `${totale}px`;
        table.style.minWidth = "";
        document.body.style.cursor = "col-resize";
        const move = (ev) => {
          const w = Math.max(48, startW + ev.clientX - startX);
          th.style.width = `${w}px`;
          table.style.width = `${totale - Math.round(startW) + Math.round(w)}px`;
        };
        const up = () => {
          document.removeEventListener("mousemove", move);
          document.removeEventListener("mouseup", up);
          document.body.style.cursor = "";
          const all = loadWidths();
          const salvate = { ...(all[id] || {}) };
          ths.forEach((o, i) => { salvate[i] = Math.round(o.getBoundingClientRect().width); });
          all[id] = salvate;
          saveWidths(all);
          resizing = false;
        };
        document.addEventListener("mousemove", move);
        document.addEventListener("mouseup", up);
      });
      th.appendChild(handle);
    }
  });

  if (bloccata) {
    const totale = ths.reduce((s, o) => s + (parseFloat(o.style.width) || o.getBoundingClientRect().width), 0);
    table.style.width = `${Math.round(totale)}px`;
  }

  applySort(table);
};

export const TableTools = () => {
  useEffect(() => {
    const scan = () => {
      if (applying || resizing) return;
      document.querySelectorAll("table").forEach((t) => {
        try { cardify(t); } catch { /* tabella non compatibile: si ignora */ }
        try { decorate(t); } catch { /* tabella non compatibile: si ignora */ }
      });
    };
    scan();
    const obs = new MutationObserver(() => { if (!applying) window.requestAnimationFrame(scan); });
    obs.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", scan);
    return () => { obs.disconnect(); window.removeEventListener("resize", scan); };
  }, []);
  return null;
};
